import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import CommonTable from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import Loaders from "components/Common/Loaders";
import { toast } from "react-toastify";
import {
    applyClientAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { useBlockFadvRequestorContext } from "libs/context/BlockFadvRequestorContext";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import {
    submitBlockFadvRequestorFilterData,
    toggleBlockFadvRequestor,
} from "model/BlockFadvRequestorModel";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const BLOCK_FADV_REQUESTOR_PAGE_NAME = "BlockFadvRequestor";

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const normalizeBlockFadvRequestorFilterPayload = (raw = {}) => ({
    nameContains: strOrNull(raw?.nameContains) ?? "",
    statusId: strOrNull(raw?.statusId),
    clientId: strOrNull(raw?.clientId) ?? "",
    pageNumber: Number(raw?.pageNumber) || 1,
    pageSize: Number(raw?.pageSize) || DEFAULT_PAGE_SIZE,
});

const normalizeBlockFadvRequestorId = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const resolveBlockFadvRequestorRowKey = (row = {}) =>
    String(row?.fadvOrderDataDetailsId ?? row?.rowId ?? row?.id ?? row?.Id ?? "").trim();

const BlockFadvRequestor = () => {
    const {
        fetchBlockFadvRequestorContextData,
        blockFadvRequestorContextData,
        loading: metadataLoading,
    } = useBlockFadvRequestorContext() || {};
    const { getDropdownOptionsByApiKey } = useCommonFilterOptions();
    const { pageSize: settingsPageSize, settingsReady } =
        useCommonTableSettingsPageSize(BLOCK_FADV_REQUESTOR_PAGE_NAME);
    const initialFilterFetchedRef = useRef(false);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableMetaData, setTableMetadata] = useState();
    const [statusOptions, setStatusOptions] = useState([{ value: "", label: "Select Client Status" }]);
    const [filterForm, setFilterForm] = useState({
        nameContains: "",
        statusId: "",
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
    });
    const [requestPayload, setRequestPayload] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        nameContains: "",
        statusId: "",
        clientId: "",
    });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });

    const filteredData = useMemo(() => rows, [rows]);

    useEffect(() => {
        fetchBlockFadvRequestorContextData?.();
    }, []);

    /** 1) settings page size ready → 2) call filter API once */
    useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getList({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    useEffect(() => {
        const valueReModified = blockFadvRequestorContextData?.table?.columns?.map((col) => {
            const field = String(col?.field ?? "").trim();
            const normalizedField = field.replace(/_/g, "").toLowerCase();
            return {
                Header: col?.title,
                accessor: col?.field,
                type: normalizedField === "clientstatus" ? "status" : col?.type,
            };
        });
        setTableMetadata(valueReModified);
    }, [blockFadvRequestorContextData]);

    useEffect(() => {
        const loadStatusOptions = async () => {
            const options = await getDropdownOptionsByApiKey("get_status_options");
            setStatusOptions([
                { value: "", label: "Select Client Status" },
                ...(Array.isArray(options) ? options : []),
            ]);
        };
        loadStatusOptions();
    }, [getDropdownOptionsByApiKey]);

    const getList = async (payloadOverrides = {}) => {
        setLoading(true);
        try {
            const payload = normalizeBlockFadvRequestorFilterPayload({
                ...requestPayload,
                ...payloadOverrides,
            });
            const response = await submitBlockFadvRequestorFilterData(payload);
            const responseData = response?.data || {};
            const listData = Array.isArray(responseData?.records)
                ? responseData.records
                : Array.isArray(responseData)
                    ? responseData
                    : [];
            const list = Array.isArray(listData) ? listData : [];
            if (response) {
                setRows(list);
                setPaginationMeta({
                    pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
                    pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
                    totalPages: Number(responseData?.totalPages) || 0,
                    totalRecords: Number(responseData?.totalRecords) || list.length,
                });
                setRequestPayload(payload);
            } else {
                setRows([]);
            }
        } catch (error) {
            toast.error(error?.message || "Unable to load blocked requestors");
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const buildPayloadFromFilterForm = (form = filterForm) =>
        normalizeBlockFadvRequestorFilterPayload({
            pageNumber: 1,
            pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
            nameContains: form?.nameContains,
            statusId: form?.statusId,
            clientId: form?.clientId,
        });

    const handleUpperSearch = () => {
        getList(buildPayloadFromFilterForm(filterForm));
    };

    const handlePageChange = async (page) => {
        await getList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getList({ pageNumber: 1, pageSize: rows });
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleClientSelect = (option) => {
        setFilterForm((prev) => applyClientAsyncFilterOptionToFormState(prev, option));
    };

    const handleToggleBlocked = async (row, value) => {
        const rowKey = resolveBlockFadvRequestorRowKey(row);
        if (!rowKey) {
            toast.error("Unable to update requestor block status");
            return;
        }

        try {
            const response = await toggleBlockFadvRequestor({
                blockFadvRequestorId: normalizeBlockFadvRequestorId(row?.blockFadvRequestorId),
                fadvOrderDataDetailsId: String(row?.fadvOrderDataDetailsId ?? ""),
                isBlocked: value,
            });

            if (response?.success) {
                const updatedBlockFadvRequestorId =
                    normalizeBlockFadvRequestorId(
                        response?.data?.blockFadvRequestorId ?? row?.blockFadvRequestorId
                    );

                setRows((prev) =>
                    (prev || []).map((item) =>
                        resolveBlockFadvRequestorRowKey(item) === rowKey
                            ? {
                                ...item,
                                isBlocked: value,
                                blockFadvRequestorId: updatedBlockFadvRequestorId,
                            }
                            : item
                    )
                );
                toast.success(response?.message || "Updated successfully");
            }
        } catch (error) {
            toast.error(error?.message || "Unable to update requestor block status");
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Block FADV Requestor</h5>
                            <hr />

                            <LegacyFilterToolbar>
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="4" className="metadata-filter-field metadata-filter-field--text">
                                        <span className="manage-user-filter-label">Search</span>
                                        <div className="metadata-filter-field__control">
                                        <Input
                                            type="text"
                                            name="nameContains"
                                            placeholder="Search by Name"
                                            className={LEGACY_FILTER_INPUT_CLASS}
                                            value={filterForm.nameContains}
                                            onChange={handleFilterFieldChange}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") handleUpperSearch();
                                            }}
                                        />
                                        </div>
                                    </Col>
                                    <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--autocomplete">
                                        <span className="manage-user-filter-label">Client</span>
                                        <div className="metadata-filter-field__control">
                                        <AsyncClientSelect
                                            key={`block-fadv-requestor-client-${filterForm.clientId}`}
                                            pageName="FadvLog"
                                            value={filterForm.clientId}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={handleClientSelect}
                                            placeholder="Select Client"
                                        />
                                        </div>
                                    </Col>
                                    <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--dropdown">
                                        <span className="manage-user-filter-label">Client Status</span>
                                        <div className="metadata-filter-field__control">
                                        <Input
                                            type="select"
                                            name="statusId"
                                            className={LEGACY_FILTER_INPUT_CLASS}
                                            value={filterForm.statusId}
                                            onChange={handleFilterFieldChange}
                                        >
                                            {statusOptions.map((option) => (
                                                <option
                                                    key={`status-${option.value}-${option.label}`}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Input>
                                        </div>
                                    </Col>
                                    <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                                        <span className="manage-user-filter-label" aria-hidden="true">
                                            &nbsp;
                                        </span>
                                        <div className="metadata-filter-toolbar__actions">
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={handleUpperSearch}
                                        >
                                            <i className="fa fa-search me-1" />
                                            Search
                                        </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading || metadataLoading ? (
                                <Loaders />
                            ) : tableMetaData?.length ? (
                                <CommonTable
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={false}
                                    isCheckboxLeftSide={false}
                                    isPagination={true}
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                    pageName={BLOCK_FADV_REQUESTOR_PAGE_NAME}
                                    onToggleAllowed={(row, value) => handleToggleBlocked(row, value)}
                                />
                            ) : (
                                <div className="py-4 text-center text-muted">
                                    Unable to load table columns.
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

BlockFadvRequestor.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(BlockFadvRequestor);
